import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Grid,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import logo from "../../images/headerlogin.png";

// Configuration constants
const CONFIG = {
  colors: {
    primary: '#0844FE',
    primaryLight: 'rgba(8, 132, 254, 0.1)',
    lightGrey: '#E0E0E0',
    greyText: '#9E9E9E',
    black: '#000000',
    white: '#FFFFFF',
    lightBlueBg: 'rgba(233, 236, 239)',
  },
  fonts: {
    primary: 'Poppins, Roboto, sans-serif',
    bold: '700',
    medium: '500',
    regular: '400',
  },
};

// Styled Components
const InvoiceContainer = styled(Paper)(() => ({
  border: `0.5px solid ${CONFIG.colors.lightGrey}`,
  padding: '16px',
  margin: '16px',
  marginTop: '20px',
  borderRadius: '10px',
  paddingBottom: '32px',
  boxShadow: '0px 0px 10px rgba(0,0,0,0.05)',
  backgroundColor: CONFIG.colors.white,
}));

const PrimaryButton = styled(Button)(() => ({
  backgroundColor: CONFIG.colors.primary,
  color: CONFIG.colors.white,
  padding: '12px 24px',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '16px',
  fontFamily: CONFIG.fonts.primary,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#0639d4',
  },
  marginTop: '16px',
  width: '100%',
}));

const InvoicePdfAdmin = ({ open, onClose, invoiceData, user }) => {
  const invoiceRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Capitalize first letter
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  // Download PDF function
  const downloadPDF = async () => {
    setIsDownloading(true);

    try {
      const input = invoiceRef.current;

      // Capture the invoice as canvas
      const canvas = await html2canvas(input, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      });

      // Calculate PDF dimensions
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');

      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);

      // Save the PDF
      pdf.save(`invoice-${invoiceData.inv_id || 'invoice'}.pdf`);

      // Show success message
      if (window.showToast) {
        window.showToast('Invoice downloaded successfully!', 'success');
      } else {
        alert('Invoice downloaded successfully!');
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      if (window.showToast) {
        window.showToast('Error downloading invoice. Please try again.', 'error');
      } else {
        alert('Error downloading invoice. Please try again.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          maxHeight: '90vh',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{
        m: 0,
        p: 2,
        backgroundColor: '#f5f5f5',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ fontFamily: CONFIG.fonts.primary, fontWeight: 600 }}>
          Invoice Preview
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: 'auto' }}>
        <Container sx={{ py: 2, fontFamily: CONFIG.fonts.primary }}>
          {/* Invoice Preview Section */}
          <Box ref={invoiceRef}>
            <InvoiceContainer elevation={0}>
              {/* Logo */}
              <Box sx={{ mb: 3 }}>
                <Box
                  component="img"
                  src={logo}
                  alt="Vertex Cars Logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='50' viewBox='0 0 150 50'%3E%3Crect width='150' height='50' fill='%230844FE' rx='8'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='white' font-family='Poppins, sans-serif' font-size='18' font-weight='bold'%3EVertex Cars%3C/text%3E%3C/svg%3E";
                  }}
                  sx={{
                    height: 50,
                    alignSelf: 'flex-start',
                  }}
                />
              </Box>

              {/* Header Section */}
              <Grid container justifyContent="space-between" alignItems="flex-start">
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontFamily: CONFIG.fonts.primary,
                      fontWeight: CONFIG.fonts.bold,
                      fontSize: '1.5rem',
                      color: CONFIG.colors.black,
                      mb: 1
                    }}
                  >
                    Vertex Invoice
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography
                    sx={{
                      fontFamily: CONFIG.fonts.primary,
                      fontWeight: CONFIG.fonts.medium,
                      fontSize: '1rem',
                      color: CONFIG.colors.greyText,
                      mt: 2
                    }}
                  >
                    Address: UK
                  </Typography>
                </Grid>
              </Grid>

              {/* Invoice ID and Location */}
              <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontFamily: CONFIG.fonts.primary,
                      fontWeight: CONFIG.fonts.medium,
                      fontSize: '0.875rem',
                      color: CONFIG.colors.greyText,
                    }}
                  >
                    Invoice
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: CONFIG.fonts.primary,
                        fontWeight: CONFIG.fonts.bold,
                        fontSize: '0.875rem',
                        color: CONFIG.colors.black,
                        ml: 0.5
                      }}
                    >
                      ID: {invoiceData.inv_id || 'N/A'}
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography
                    sx={{
                      fontFamily: CONFIG.fonts.primary,
                      fontWeight: CONFIG.fonts.medium,
                      fontSize: '1rem',
                      color: CONFIG.colors.greyText,
                    }}
                  >
                    London
                  </Typography>
                </Grid>
              </Grid>

              {/* Blue Info Box */}
              <Box sx={{
                mt: 3,
                borderRadius: 2,
                backgroundColor: CONFIG.colors.primaryLight,
                p: 2.5,
                pb: 5,
              }}>
                <Grid container justifyContent="space-between">
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: CONFIG.fonts.primary,
                        fontWeight: CONFIG.fonts.regular,
                        fontSize: '0.75rem',
                        color: CONFIG.colors.black,
                        opacity: 0.5,
                        mb: 0.5
                      }}
                    >
                      INVOICE DETAILS
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: CONFIG.fonts.primary,
                        fontWeight: CONFIG.fonts.medium,
                        fontSize: '0.875rem',
                        color: CONFIG.colors.black,
                        mb: 1
                      }}
                    >
                      INV-{invoiceData.inv_id || '16'}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: CONFIG.fonts.primary,
                        fontWeight: CONFIG.fonts.regular,
                        fontSize: '0.75rem',
                        color: CONFIG.colors.black,
                        opacity: 0.5,
                        display: 'block'
                      }}
                    >
                      Issue Date: {formatDate(invoiceData.created_at)}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: CONFIG.fonts.primary,
                        fontWeight: CONFIG.fonts.regular,
                        fontSize: '0.75rem',
                        color: CONFIG.colors.black,
                        opacity: 0.5,
                        display: 'block'
                      }}
                    >
                      Due Date: {formatDate(invoiceData.due_date)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} textAlign="right">
                    <Typography
                      sx={{
                        fontFamily: CONFIG.fonts.primary,
                        fontWeight: CONFIG.fonts.regular,
                        fontSize: '0.75rem',
                        color: CONFIG.colors.black,
                        opacity: 0.5,
                        mb: 0.5
                      }}
                    >
                      BILLED TO
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: CONFIG.fonts.primary,
                        fontWeight: CONFIG.fonts.regular,
                        fontSize: '0.75rem',
                        color: CONFIG.colors.black,
                        opacity: 0.5,
                        display: 'block'
                      }}
                    >
                      {invoiceData.cust_name || 'Customer Name'}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: CONFIG.fonts.primary,
                        fontWeight: CONFIG.fonts.regular,
                        fontSize: '0.75rem',
                        color: CONFIG.colors.black,
                        opacity: 0.5,
                        display: 'block'
                      }}
                    >
                      {invoiceData.cust_phone || 'Customer Phone'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Items Table */}
              <Box sx={{ mt: 3 }}>
                {/* Table Headers */}
                {/* Items Table - Fixed solution */}
                <Box sx={{ mt: 3 }}>
                  {/* Table Headers */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1
                  }}>
                    <Box sx={{ width: '40%' }}>
                      <Typography
                        sx={{
                          fontFamily: CONFIG.fonts.primary,
                          fontWeight: CONFIG.fonts.regular,
                          fontSize: '1rem',
                          color: 'rgba(0, 0, 0, 0.5)'
                        }}
                      >
                        Car
                      </Typography>
                    </Box>
                    <Box sx={{ width: '25%', textAlign: 'right', marginLeft: '2px' }}>
                      <Typography
                        sx={{
                          fontFamily: CONFIG.fonts.primary,
                          fontWeight: CONFIG.fonts.regular,
                          fontSize: '1rem',
                          color: 'rgba(0, 0, 0, 0.5)'
                        }}
                      >
                        Amount
                      </Typography>
                    </Box>
                    <Box sx={{ width: '25%', textAlign: 'right', marginLeft: '2px' }}>
                      <Typography
                        sx={{
                          fontFamily: CONFIG.fonts.primary,
                          fontWeight: CONFIG.fonts.regular,
                          fontSize: '1rem',
                          color: 'rgba(0, 0, 0, 0.5)'
                        }}
                      >
                        Total
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2, backgroundColor: CONFIG.colors.lightGrey }} />

                  {/* Table Row - Same width as headers */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <Box sx={{ width: '40%' }}>
                      <Typography
                        sx={{
                          fontFamily: CONFIG.fonts.primary,
                          fontWeight: CONFIG.fonts.regular,
                          fontSize: '1rem',
                          color: 'rgba(0, 0, 0, 0.8)',
                          wordWrap: 'break-word'
                        }}
                      >
                        {invoiceData.make || 'Car Make'} {invoiceData.model || 'Car Model'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '25%', textAlign: 'right', marginLeft: '2px' }}>
                      <Typography
                        sx={{
                          fontFamily: CONFIG.fonts.primary,
                          fontWeight: CONFIG.fonts.regular,
                          fontSize: '1rem',
                          color: 'rgba(0, 0, 0, 0.8)'
                        }}
                      >
                        £{invoiceData.inv_amount || '0'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '25%', textAlign: 'right', marginLeft: '2px' }}>
                      <Typography
                        sx={{
                          fontFamily: CONFIG.fonts.primary,
                          fontWeight: CONFIG.fonts.regular,
                          fontSize: '1rem',
                          color: 'rgba(0, 0, 0, 0.8)'
                        }}
                      >
                        £{invoiceData.inv_amount || '0'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {/* Total Amount Box */}
                <Box sx={{
                  mt: 2.5,
                  backgroundColor: CONFIG.colors.lightBlueBg,
                  borderRadius: 2,
                  height: 50,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  px: 2,
                }}>
                  <Typography
                    sx={{
                      fontFamily: CONFIG.fonts.primary,
                      fontWeight: CONFIG.fonts.medium,
                      fontSize: '0.9375rem',
                      color: 'rgba(0, 0, 0, 0.5)'
                    }}
                  >
                    Total Amount:
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: CONFIG.fonts.primary,
                        fontWeight: CONFIG.fonts.medium,
                        fontSize: '0.9375rem',
                        color: CONFIG.colors.black,
                        ml: 1
                      }}
                    >
                      £{invoiceData.inv_amount || '0'}
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </InvoiceContainer>
          </Box>

          {/* Status and Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2 }}>
            <Typography
              sx={{
                fontFamily: CONFIG.fonts.primary,
                fontWeight: CONFIG.fonts.regular,
                fontSize: '1rem',
                color: CONFIG.colors.black,
              }}
            >
              Status: {capitalizeFirstLetter(invoiceData?.status || 'pending')}
            </Typography>
          </Box>

          {/* Download Button */}
          <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', mt: 3 }}>
            <PrimaryButton
              onClick={downloadPDF}
              disabled={isDownloading}
              startIcon={
                isDownloading ?
                  <CircularProgress size={20} color="inherit" /> :
                  <DownloadIcon />
              }
            >
              {isDownloading ? 'Downloading...' : 'Download PDF'}
            </PrimaryButton>
          </Box>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicePdfAdmin;